import { skipToken } from "@reduxjs/toolkit/query/react";
import { QueryStatus } from "@sscs/types";
import { compareValues, getQueryStatus } from "@sscs/utils";
import { ORGANIZATION_CATALOG_ID, PERMISSION } from "Constants";
import { useAllowedTo } from "Hooks";
import { useGetPackageQuery, useGetVersionQuery } from "Services";
import { Package, Version } from "Types";
import { getCatalogRoute, getExtension } from "Utils";
import { useGetSoftwareCatalogPlatformsQuery } from "../services/softwareCatalog.api";
import {
  Catalog,
  CatalogNode,
  Catalogs,
  ExternalCatalogPlatform,
  SoftwareCatalogPlatform,
} from "../types";
import useCatalogParams from "./useCatalogParams";

const PLATFORM_ORDER = [
  "Windows",
  "Linux",
  "MacOS",
  "Containers",
  "Browser",
  "Mobile",
  "AI",
  "Other",
];

const platformRank = (name: string): number => {
  const index = PLATFORM_ORDER.indexOf(name);

  return index === -1 ? PLATFORM_ORDER.length : index;
};

export type SelectedCatalog = {
  catalogId: string;
  catalogName: string;
  path: CatalogNode[];
};

type Counted =
  | Pick<SoftwareCatalogPlatform, "in_catalog_count" | "total_count">
  | (Pick<ExternalCatalogPlatform, "count"> & { total_count?: undefined });

const counts = (item: Counted): Pick<CatalogNode, "count" | "total"> =>
  item.total_count !== undefined
    ? { count: item.in_catalog_count, total: item.total_count }
    : { count: item.count, total: null };

const node = ({
  name,
  label = name,
  route,
  counted,
  children,
}: {
  name: string;
  label?: string;
  route: string;
  counted: Counted;
  children: Record<string, CatalogNode>;
}): CatalogNode => ({ name, label, route, ...counts(counted), children });

const byName = (nodes: CatalogNode[]): Record<string, CatalogNode> =>
  Object.fromEntries(nodes.map((item) => [item.name, item]));

const toCatalog = (
  groupId: string,
  catalogId: string,
  title: string,
  platforms: SoftwareCatalogPlatform[] | ExternalCatalogPlatform[],
): Catalog => ({
  catalogId,
  title,
  platforms: byName(
    platforms
      .map((platform) =>
        node({
          name: platform.platform,
          counted: platform,
          route: getCatalogRoute(groupId, {
            catalogId,
            platform: platform.platform,
          }),
          children: byName(
            platform.architectures
              .map((architecture) =>
                node({
                  name: architecture.architecture,
                  counted: architecture,
                  route: getCatalogRoute(groupId, {
                    catalogId,
                    platform: platform.platform,
                    architecture: architecture.architecture,
                  }),
                  children: byName(
                    architecture.extensions
                      .map((extension) =>
                        node({
                          name: extension.extension,
                          label: extension.extension
                            ? `.${extension.extension}`
                            : "No extension",
                          counted: extension,
                          route: getCatalogRoute(groupId, {
                            catalogId,
                            platform: platform.platform,
                            architecture: architecture.architecture,
                            extension: extension.extension,
                          }),
                          children: {},
                        }),
                      )
                      .sort((first, second) =>
                        compareValues(first.name, second.name, false),
                      ),
                  ),
                }),
              )
              .sort((first, second) =>
                compareValues(first.name, second.name, false),
              ),
          ),
        }),
      )
      .sort((first, second) =>
        compareValues(
          platformRank(first.name),
          platformRank(second.name),
          false,
        ),
      ),
  ),
});

const findSelectedCatalog = (
  catalogs: Catalogs,
  catalogId: string | undefined,
  platform: string | undefined,
  architecture: string | null | undefined,
  extension: string | null | undefined,
): SelectedCatalog | undefined => {
  if (!catalogId) return undefined;

  const catalog: Catalog | undefined = catalogs[catalogId];

  if (!catalog) return undefined;

  const platformNode: CatalogNode | undefined = platform
    ? catalog.platforms[platform]
    : undefined;

  if (platform && !platformNode) return undefined;

  const architectureNode: CatalogNode | undefined = architecture
    ? platformNode?.children[architecture]
    : undefined;
  const extensionNode = Object.values(architectureNode?.children ?? {}).find(
    (item) => item.name.toLowerCase() === extension?.toLowerCase(),
  );

  return {
    catalogId,
    catalogName: catalog.title,
    path: [platformNode, architectureNode, extensionNode].filter(
      (item): item is CatalogNode => Boolean(item),
    ),
  };
};

const useCatalogs = (): {
  catalogs: Catalogs | undefined;
  status: QueryStatus;
  selectedCatalog: SelectedCatalog | undefined;
  catalogPackage: Package | undefined;
  catalogVersion: Version | undefined;
} => {
  const { groupId, catalogId, platform, architecture, extension, packageId } =
    useCatalogParams();
  const result = useGetSoftwareCatalogPlatformsQuery(
    groupId ? { groupId } : skipToken,
  );
  const canCurateCatalog = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });
  const { currentData: catalogPackage } = useGetPackageQuery(
    packageId ? { packageId } : skipToken,
  );
  const { currentData: catalogVersion } = useGetVersionQuery(
    catalogPackage?.catalog_version_id
      ? { versionId: catalogPackage.catalog_version_id }
      : skipToken,
  );
  const status = getQueryStatus(result);

  if (!groupId || !result.data) {
    return {
      catalogs: undefined,
      selectedCatalog: undefined,
      status,
      catalogPackage,
      catalogVersion,
    };
  }

  const catalogs: Catalogs = {
    [ORGANIZATION_CATALOG_ID]: toCatalog(
      groupId,
      ORGANIZATION_CATALOG_ID,
      "My group",
      result.data.org_catalog,
    ),
    ...(canCurateCatalog
      ? Object.fromEntries(
          result.data.external_catalogs.map((catalog) => [
            catalog.catalog,
            toCatalog(
              groupId,
              catalog.catalog,
              catalog.display_name ?? catalog.catalog,
              catalog.platforms,
            ),
          ]),
        )
      : {}),
  };

  return {
    catalogs,
    status,
    selectedCatalog: findSelectedCatalog(
      catalogs,
      catalogId,
      platform,
      architecture ?? catalogVersion?.software_info.architecture,
      extension ??
        (catalogVersion && (getExtension(catalogVersion.file_name) ?? "")),
    ),
    catalogPackage,
    catalogVersion,
  };
};

export default useCatalogs;
